import React, { useEffect, useState } from "react";
import { Link, NavLink, Outlet, useLocation } from "react-router-dom";
import { AnimatePresence, motion } from "framer-motion";
import { ROUTES, SITE, fullAddress } from "../content";
import { SealStamp, NoodleDivider } from "./Art";

const NAV = [
  { to: "/", label: "Beranda", id: "nav-beranda" },
  { to: "/menu/", label: "Menu", id: "nav-menu" },
  { to: "/tentang/", label: "Tentang", id: "nav-tentang" },
  { to: "/lokasi/", label: "Lokasi", id: "nav-lokasi" },
  { to: "/kontak/", label: "Kontak", id: "nav-kontak" },
  { to: "/faq/", label: "FAQ", id: "nav-faq" },
];

export function Breadcrumbs({ current }) {
  return (
    <nav aria-label="Breadcrumb" className="breadcrumbs mono">
      <Link to="/" data-testid="breadcrumb-home">
        Beranda
      </Link>
      <span aria-hidden="true"> / </span>
      <span aria-current="page">{current}</span>
    </nav>
  );
}

function Header() {
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header className="site-header">
      <div className="site-header__bar">
        <Link to="/" className="brand" data-testid="brand-home-link" aria-label="Bakmi Aboen — Beranda">
          <span className="brand__seal" aria-hidden="true">
            A
          </span>
          <span className="brand__name font-display">Bakmi Aboen</span>
        </Link>
        <nav className="site-nav" aria-label="Navigasi utama">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.id}
              className={({ isActive }) => `site-nav__link${isActive ? " is-active" : ""}`}
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="site-header__actions">
          <Link to="/lokasi/" className="btn btn--seal btn--sm" data-testid="header-cta-kunjungi">
            Kunjungi Kedai
          </Link>
          <button
            type="button"
            className="menu-toggle"
            aria-expanded={open}
            aria-label={open ? "Tutup menu navigasi" : "Buka menu navigasi"}
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
          >
            <span />
            <span />
          </button>
        </div>
      </div>
      <AnimatePresence>
        {open && (
          <motion.nav
            className="mobile-nav"
            aria-label="Navigasi seluler"
            initial={{ opacity: 0, y: -12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -12 }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
          >
            {NAV.map((n, i) => (
              <motion.div
                key={n.to}
                initial={{ opacity: 0, x: -18 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: 0.05 + i * 0.05, duration: 0.4 }}
              >
                <NavLink
                  to={n.to}
                  end={n.to === "/"}
                  data-testid={`mobile-${n.id}`}
                  className={({ isActive }) => `mobile-nav__link font-display${isActive ? " is-active" : ""}`}
                >
                  <span className="mono mobile-nav__no">0{i + 1}</span>
                  {n.label}
                </NavLink>
              </motion.div>
            ))}
            <p className="mobile-nav__meta mono">
              Est. {SITE.established} · Non-Halal · {SITE.hoursDisplay}
            </p>
          </motion.nav>
        )}
      </AnimatePresence>
    </header>
  );
}

function Footer() {
  return (
    <footer className="site-footer">
      <div className="site-footer__giant font-display" aria-hidden="true">
        BAKMI&nbsp;ABOEN
      </div>
      <div className="site-footer__grid">
        <div>
          <h2 className="mono site-footer__heading">Alamat</h2>
          <p>
            {SITE.address.street}
            <br />
            {SITE.address.area}
            <br />
            {SITE.address.city}, {SITE.address.region}
          </p>
        </div>
        <div>
          <h2 className="mono site-footer__heading">Jam Buka</h2>
          <p>
            Senin–Minggu
            <br />
            10.00–22.00 WIB
          </p>
        </div>
        <div>
          <h2 className="mono site-footer__heading">Kontak</h2>
          <p>
            <a href={SITE.phoneHref} data-testid="footer-phone-link">
              {SITE.phoneDisplay}
            </a>
            <br />
            <a href={SITE.mapsUrl} target="_blank" rel="noopener noreferrer" data-testid="footer-maps-link">
              Google Maps
            </a>
          </p>
        </div>
        <div>
          <h2 className="mono site-footer__heading">Jelajahi</h2>
          <p className="site-footer__links">
            {NAV.map((n) => (
              <Link key={n.to} to={n.to} data-testid={`footer-${n.id}`}>
                {n.label}
              </Link>
            ))}
          </p>
        </div>
      </div>
      <NoodleDivider className="site-footer__divider" />
      <div className="site-footer__base mono">
        <span>© 2026 Bakmi Aboen · Pasar Baru, Jakarta Pusat</span>
        <span>Seluruh menu non-halal (mengandung babi)</span>
      </div>
    </footer>
  );
}

export default function Layout() {
  const location = useLocation();
  useEffect(() => {
    const route = ROUTES.find((r) => r.path === location.pathname);
    document.title = route
      ? route.title
      : "Halaman Tidak Ditemukan — Bakmi Aboen";
  }, [location.pathname]);

  return (
    <div className="site-shell">
      <a href="#konten" className="skip-link">
        Lewati ke konten
      </a>
      <Header />
      <main id="konten">
        <Outlet />
      </main>
      <Footer />
      <SealStamp className="site-shell__stamp" />
    </div>
  );
}

export { fullAddress };
